import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  fetchCorporateClient,
  downloadCorporatePdf,
  sendSingleCorporateEmail,
} from '../../api/index'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import styles from './CorporateClients.module.css'
import campStyles from '../ImportMensuel/CampagneDetail.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'sent', label: 'Envoyées' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échecs' },
]

const statusLabel = {
  sent: { text: 'Envoyée', cls: 'sent' },
  failed: { text: 'Échec', cls: 'failed' },
  pending: { text: 'En attente', cls: 'pending' },
}

const clientInitial = (name) => (name?.trim()?.charAt(0)?.toUpperCase() || '?')

const formatPeriod = (campaign) => {
  if (!campaign) return '—'
  if (campaign.period) return campaign.period
  if (campaign.month && campaign.year) return `${MONTHS[campaign.month]} ${campaign.year}`
  return campaign.label || '—'
}

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const CorporateClientDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [sending, setSending] = useState(false)
  const limit = 10

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await fetchCorporateClient(id, { page, limit, status: statusFilter })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Client introuvable')
      history.push('/corporate/clients')
    } finally {
      setLoading(false)
    }
  }, [id, page, statusFilter, history])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page, statusFilter])

  useEffect(() => {
    setPage(1)
  }, [statusFilter])

  const downloadPdf = async (campaignId, documentId, fileName) => {
    if (!campaignId) {
      openSnackbar('PDF non disponible')
      return
    }
    try {
      const response = await downloadCorporatePdf(campaignId, documentId)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      window.URL.revokeObjectURL(url)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const sendOne = async (campaignId, documentId) => {
    if (!campaignId) {
      openSnackbar('Campagne introuvable')
      return
    }
    setSending(true)
    try {
      const { data: result } = await sendSingleCorporateEmail(campaignId, documentId)
      openSnackbar(result.message)
      load()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi')
    } finally {
      setSending(false)
    }
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data?.client) return null

  const { client, documents = [], stats = {}, pagination = {} } = data
  const totalPages = pagination.totalPages || 1
  const canSendEmail = Boolean(client.email)

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/corporate/clients')}>
        ← Retour aux clients
      </button>

      <div className={styles.topBar}>
        <div className={styles.clientCell}>
          <span className={styles.avatar} aria-hidden="true">{clientInitial(client.name)}</span>
          <div>
            <h1>{client.name}</h1>
            <p>
              {client.visAVis ? `Vis-à-vis : ${client.visAVis}` : 'Vis-à-vis non renseigné'}
              {client.matriculeFiscal ? ` · MF : ${client.matriculeFiscal}` : ''}
            </p>
            <p>
              {client.address || 'Adresse non renseignée'}
            </p>
            <p>
              {client.email
                ? <>Email facturation : <span className={styles.badgeOk}>{client.email}</span></>
                : <span className={styles.badgeErr}>Email manquant — envoi impossible</span>}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Factures PDF</span>
          <span className={styles.kpiValue}>{stats.totalDocuments ?? 0}</span>
          <span className={styles.kpiSub}>Documents Sage X3</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiOk}`}>
          <span className={styles.kpiLabel}>Envoyées</span>
          <span className={styles.kpiValue}>{stats.sent ?? 0}</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiWarn}`}>
          <span className={styles.kpiLabel}>En attente</span>
          <span className={styles.kpiValue}>{stats.pending ?? 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Échecs</span>
          <span className={styles.kpiValue}>{stats.failed ?? 0}</span>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Historique des factures</h2>
            <p className={styles.hint}>
              {pagination.total ?? 0} document(s) PDF pour ce client
              {statusFilter ? ` · filtre : ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}` : ''}
            </p>
          </div>
        </div>

        <div className={campStyles.toolbar}>
          <select
            className={campStyles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrer par statut d'envoi"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {statusFilter && (
            <button
              type="button"
              className={campStyles.clearBtn}
              onClick={() => setStatusFilter('')}
            >
              Réinitialiser
            </button>
          )}
        </div>

        {documents.length === 0 ? (
          <p className={styles.empty}>Aucune facture PDF enregistrée pour ce client.</p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>Période / Campagne</th>
                    <th>Fichier PDF</th>
                    <th>Statut envoi</th>
                    <th>Date envoi</th>
                    <th className={styles.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const st = statusLabel[doc.emailStatus || 'pending']
                    const campaignId = doc.campaignId?._id || doc.campaignId
                    return (
                      <tr key={doc._id}>
                        <td data-label="Période / Campagne">
                          <span>{formatPeriod(doc.campaignId)}</span>
                          {campaignId && (
                            <button
                              type="button"
                              className={styles.clientLink}
                              onClick={() => history.push(`/corporate/campagnes/${campaignId}`)}
                            >
                              Voir campagne
                            </button>
                          )}
                        </td>
                        <td data-label="Fichier PDF">{doc.fileName}</td>
                        <td data-label="Statut envoi">
                          <span className={`${campStyles.badge} ${campStyles[st.cls]}`}>{st.text}</span>
                          {doc.emailError && (
                            <span className={styles.clientHint} title={doc.emailError}> · {doc.emailError}</span>
                          )}
                        </td>
                        <td data-label="Date envoi">{formatDate(doc.emailSentAt)}</td>
                        <td className={styles.actionsCol} data-label="Actions">
                          <div className={`${styles.actionCell} action-cell-inner`}>
                            <button
                              type="button"
                              className={campStyles.pdfBtn}
                              onClick={() => downloadPdf(campaignId, doc._id, doc.fileName)}
                            >
                              PDF
                            </button>
                            <button
                              type="button"
                              className={campStyles.emailBtn}
                              disabled={sending || !canSendEmail}
                              onClick={() => sendOne(campaignId, doc._id)}
                            >
                              {doc.emailStatus === 'sent' ? 'Renvoyer' : 'Envoyer'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <div className={styles.paginationLeft}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← Précédent
                  </button>
                  <span className={styles.pageInfo}>Page {page} / {totalPages}</span>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CorporateClientDetail
